import React, { useState } from 'react';
import Webcam from 'react-webcam';
import { Box, IconButton, Typography } from '@mui/material';
import MessageViewer from '@shared/messageViewer';
import { UseEnhancedWebcamReturn } from './useEnhancedWebcam';
import { WEBCAM_CAPTURE_SIZE, WEBCAM_VIDEO_CONSTRAINTS, DEBUG_BLUR_DETECTION, BLUR_THRESHOLD } from './imageUtils';

interface WebcamCaptureOverlayProps {
  webcam: UseEnhancedWebcamReturn;
}

/**
 * Full-screen webcam capture overlay with:
 * - Document alignment guide frame
 * - Countdown timer
 * - Blur detection warning
 * - Tap-to-focus capability
 * - Focus lock status indicator
 * - Retake / Use Photo after capture
 */
export const WebcamCaptureOverlay: React.FC<WebcamCaptureOverlayProps> = ({ webcam }) => {
  const {
    webcamRef,
    showWebcam,
    webcamPreview,
    webcamCountdown,
    isWebcamCapturing,
    webcamBlurWarning,
    lastSharpnessRef,
    handleCloseWebcam,
    startWebcamCountdown,
    handleWebcamRetake,
    handleWebcamUsePhoto,
  } = webcam;

  const [streamInfo, setStreamInfo] = useState<{ width?: number; height?: number; frameRate?: number } | null>(null);
  const [isFocusReady, setIsFocusReady] = useState(false);

  if (!showWebcam) return null;

  const handleUserMedia = (stream: MediaStream) => {
    const [videoTrack] = stream.getVideoTracks();
    if (!videoTrack) return;
    const settings = videoTrack.getSettings();
    setStreamInfo(settings);

    const status = settings.width && settings.height ? `${Math.round(settings.width)}x${Math.round(settings.height)}` : 'unknown';
    console.info("[WebcamCaptureOverlay] 📹 Camera stream active:", status, "FPS:", settings.frameRate);
    console.info("[WebcamCaptureOverlay] Focus Mode:", (settings as any).focusMode, "Focus Distance:", (settings as any).focusDistance);

    // Give autofocus time to engage (typically 500-1500ms on mobile)
    console.info("[WebcamCaptureOverlay] ⏳ Waiting for autofocus to lock...");
    setIsFocusReady(false);
    setTimeout(() => {
      setIsFocusReady(true);
      console.info("[WebcamCaptureOverlay] ✅ Autofocus locked, camera ready");
    }, 1500);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'black',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh',
        minHeight: '100svh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1300,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {webcamPreview ? (
          <Box
            component="img"
            src={webcamPreview}
            alt="Captured preview"
            sx={{ width: '99%', height: '40%', objectFit: 'contain' }}
          />
        ) : (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/png"
              screenshotQuality={1}
              forceScreenshotSourceSize
              minScreenshotWidth={WEBCAM_CAPTURE_SIZE.width}
              minScreenshotHeight={WEBCAM_CAPTURE_SIZE.height}
              imageSmoothing={false}
              onUserMedia={handleUserMedia}
              style={{ width: '99%', height: '40%', objectFit: 'cover', cursor: 'crosshair' }}
              videoConstraints={WEBCAM_VIDEO_CONSTRAINTS}
            />

            {/* Document alignment overlay */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
             
              <Box
                sx={{
                  position: 'relative',
                  width: '99%',
                  height: '40%',
                  bottom: "2px",
                  aspectRatio: '3 / 2',
                  maxHeight: '60%',
                  border: '2px dashed rgba(255, 255, 255, 0.8)',
                  borderRadius: '8px',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                }}
              >
         
                <Box sx={{ position: 'absolute', top: -2, left: -2, width: 24, height: 24, borderTop: '4px solid #fff', borderLeft: '4px solid #fff', borderTopLeftRadius: 8 }} />
                <Box sx={{ position: 'absolute', top: -2, right: -2, width: 24, height: 24, borderTop: '4px solid #fff', borderRight: '4px solid #fff', borderTopRightRadius: 8 }} />
                <Box sx={{ position: 'absolute', bottom: -2, left: -2, width: 24, height: 24, borderBottom: '4px solid #fff', borderLeft: '4px solid #fff', borderBottomLeftRadius: 8 }} />
                <Box sx={{ position: 'absolute', bottom: -2, right: -2, width: 24, height: 24, borderBottom: '4px solid #fff', borderRight: '4px solid #fff', borderBottomRightRadius: 8 }} />
              </Box>
            </Box>

            {/* Countdown overlay */}
            {webcamCountdown !== null && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <Typography sx={{ fontSize: '6rem', fontWeight: 700, color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}>
                  {webcamCountdown}
                </Typography>
              </Box>
            )}

            {/* Blur warning */}
            {webcamBlurWarning && (
              <Box sx={{ position: 'absolute', bottom: 'calc(96px + env(safe-area-inset-bottom))', left: 16, right: 16 }}>
                <MessageViewer status="error" details={webcamBlurWarning} />
              </Box>
            )}

            {/* Debug sharpness score badge */}
            {DEBUG_BLUR_DETECTION && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: lastSharpnessRef.current ? (lastSharpnessRef.current >= BLUR_THRESHOLD ? '#4CAF50' : '#FF6B6B') : '#FFF',
                  px: 2,
                  py: 1,
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  zIndex: 10,
                }}
              >
                Sharpness: {lastSharpnessRef.current ? Math.round(lastSharpnessRef.current) : '—'}
                {' '}({BLUR_THRESHOLD})
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 5,
          pt: 2,
          pb: 'max(16px, env(safe-area-inset-bottom))',
          backgroundColor: 'rgba(0,0,0,0.85)',
        }}
      >
        {webcamPreview ? (
          <>
            <IconButton
              onClick={handleWebcamRetake}
              aria-label="Retake photo"
              sx={{ width: 56, height: 56, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', '&:hover': { backgroundColor: '#f5f5f5' } }}
            >
              {/* Retry/refresh icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="#007CBF"/>
              </svg>
            </IconButton>
            <IconButton
              onClick={handleWebcamUsePhoto}
              aria-label="Use photo"
              sx={{ width: 56, height: 56, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', '&:hover': { backgroundColor: '#f5f5f5' } }}
            >
              {/* Checkmark icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="#007CBF"/>
              </svg>
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              onClick={handleCloseWebcam}
              aria-label="Cancel"
              sx={{ width: 48, height: 48, backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}
            >
              <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 500 }}>Cancel</Typography>
            </IconButton>
            <IconButton
              onClick={startWebcamCountdown}
              disabled={isWebcamCapturing || !isFocusReady}
              aria-label="Capture photo"
              sx={{ width: 68, height: 68, border: '4px solid #fff', backgroundColor: 'transparent', p: 0, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { opacity: 0.5 } }}
            >
              <Box sx={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: '#d4d4d4' }} />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
};
